/* eslint-disable react-hooks/exhaustive-deps */
import styles from 'styles/components/onboarding.module.scss';
import { useContext, useEffect, useState } from 'react';
import Welcome from './welcome';
import EnterDomain from './enterDomain';
import VerifyDomain from './verifyDomain';
import DomainContext from 'services/domainContext';
import { LOCAL_STORAGE } from 'utils/constants';
import Loader from 'components/loader';
import VerifyLinkedin from './verifyLinkedin';
import UnderReview from './underReview';
import RejectedValidation from './rejected';
import ProfileContext from 'services/profileContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, message } from 'antd';
import Logo from 'components/logo';
import { logout } from 'services/api/users';
import { CloseOutlined } from '@ant-design/icons';
import {
  PROFILE_VALIDATION_STATUS,
  getProfileValidationStatus,
  shouldShowValidationWall,
} from 'utils/profileValidation';

const STEPS = {
  WELCOME: 'WELCOME',
  ENTER_DOMAIN: 'ENTER_DOMAIN',
  VERIFY_DOMAIN: 'VERIFY_DOMAIN',
  LINKEDIN: 'LINKEDIN',
  UNDER_REVIEW: 'UNDER_REVIEW',
  REJECTED: 'REJECTED',
};

const Onboarding = ({ firstOnboarding = false, close = () => {} }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.WELCOME);
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const { domains } = useContext(DomainContext);
  const { profile, clearProfile } = useContext(ProfileContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const comingFromLinkedinConnect = searchParams.get('linkedin_connect');

  const alreadyHasOneDomainVerified =
    domains?.filter((e) => e.verified === true).length > 0;

  const alreadyHasOneDomainApproved =
    domains?.filter((e) => e.status === 'APPROVED').length > 0;

  const canDismissOnboarding = alreadyHasOneDomainApproved;

  const goToPostSetupStep = () => {
    const status = getProfileValidationStatus(profile, domains);
    if (
      status === PROFILE_VALIDATION_STATUS.REJECTED &&
      !alreadyHasOneDomainApproved
    ) {
      setCurrentStep(STEPS.REJECTED);
      return;
    }
    if (shouldShowValidationWall(profile, domains)) {
      setCurrentStep(STEPS.UNDER_REVIEW);
      return;
    }
    if (!firstOnboarding && alreadyHasOneDomainApproved) {
      setCurrentStep(STEPS.UNDER_REVIEW);
      return;
    }
    close();
  };

  useEffect(() => {
    // initial load, determine which step suits best
    //only redirect if this is the FIRST Onboarding
    if (!firstLoadDone) {
      setFirstLoadDone(true);
      // If coming back from linkedin connect, go to linkedin step
      // https://app.karmalinks.io/#/dashboard?linkedin_connect=true
      if (comingFromLinkedinConnect && comingFromLinkedinConnect === 'true') {
        setCurrentStep(STEPS.LINKEDIN);
        setSearchParams((prev) => {
          prev.delete('linkedin_connect');
          return prev;
        });
      } else if (
        comingFromLinkedinConnect &&
        comingFromLinkedinConnect === 'error'
      ) {
        setCurrentStep(STEPS.LINKEDIN);
        message.error('Linkedin connection failed, please try again.');
      } else {
        const currentDomainName = localStorage.getItem(
          LOCAL_STORAGE.CURRENT_DOMAIN_VALIDATION
        );
        const setupComplete =
          alreadyHasOneDomainVerified && !!profile.linkedinProfile;
        const status = getProfileValidationStatus(profile, domains);
        if (
          setupComplete &&
          (shouldShowValidationWall(profile, domains) ||
            status === PROFILE_VALIDATION_STATUS.REJECTED)
        ) {
          goToPostSetupStep();
        } else if (!currentDomainName || currentDomainName.length === 0) {
          setCurrentStep(
            alreadyHasOneDomainVerified ? STEPS.ENTER_DOMAIN : STEPS.WELCOME
          );
        } else if (
          currentDomainName &&
          currentDomainName.length > 0 &&
          !!domains?.find((d) => d.name === currentDomainName) &&
          !domains.find((d) => d.name === currentDomainName).verified
        ) {
          setCurrentStep(STEPS.VERIFY_DOMAIN);
        } else if (
          currentDomainName &&
          currentDomainName.length > 0 &&
          !!domains?.find((d) => d.name === currentDomainName) &&
          domains.find((d) => d.name === currentDomainName).verified &&
          !profile.linkedinProfile
        ) {
          setCurrentStep(STEPS.LINKEDIN);
        } else if (
          currentDomainName &&
          currentDomainName.length > 0 &&
          !!domains?.find((d) => d.name === currentDomainName) &&
          domains.find((d) => d.name === currentDomainName).verified &&
          profile.linkedinProfile
        ) {
          goToPostSetupStep();
        } else if (
          currentDomainName &&
          currentDomainName.length > 0 &&
          !domains?.find((d) => d.name === currentDomainName)
        ) {
          setCurrentStep(STEPS.ENTER_DOMAIN);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!firstLoadDone) return;

    const setupComplete =
      alreadyHasOneDomainVerified && !!profile.linkedinProfile;
    const status = getProfileValidationStatus(profile, domains);

    if (setupComplete && shouldShowValidationWall(profile, domains)) {
      if (currentStep !== STEPS.UNDER_REVIEW) {
        setCurrentStep(STEPS.UNDER_REVIEW);
      }
      return;
    }

    if (
      setupComplete &&
      status === PROFILE_VALIDATION_STATUS.REJECTED &&
      !alreadyHasOneDomainApproved
    ) {
      if (currentStep !== STEPS.REJECTED) {
        setCurrentStep(STEPS.REJECTED);
      }
      return;
    }

    if (
      currentStep !== STEPS.UNDER_REVIEW &&
      currentStep !== STEPS.REJECTED
    ) {
      return;
    }
    goToPostSetupStep();
  }, [profile, domains, currentStep, firstLoadDone]);

  const nextStep = () => {
    if (currentStep === STEPS.UNDER_REVIEW || currentStep === STEPS.REJECTED) {
      return;
    }
    if (currentStep === STEPS.LINKEDIN) {
      goToPostSetupStep();
      return;
    }
    const vals = Object.values(STEPS);
    const indexOfCurrentStep = vals.indexOf(currentStep);
    setCurrentStep(STEPS[vals[indexOfCurrentStep + 1]]);
  };

  const previousStep = () => {
    if (currentStep === STEPS.WELCOME) {
      return;
    }
    const vals = Object.values(STEPS);
    const indexOfCurrentStep = vals.indexOf(currentStep);
    setCurrentStep(STEPS[vals[indexOfCurrentStep - 1]]);
  };

  const getStepContent = () => {
    switch (currentStep) {
      default:
      case STEPS.WELCOME:
        return <Welcome nextStep={nextStep} />;
      case STEPS.ENTER_DOMAIN:
        return <EnterDomain nextStep={nextStep} />;
      case STEPS.VERIFY_DOMAIN:
        return <VerifyDomain nextStep={nextStep} previousStep={previousStep} />;
      case STEPS.LINKEDIN:
        return (
          <VerifyLinkedin nextStep={nextStep} previousStep={previousStep} />
        );
      case STEPS.UNDER_REVIEW:
        return <UnderReview close={close} />;
      case STEPS.REJECTED:
        return <RejectedValidation />;
    }
  };
  const navigate = useNavigate();
  return (
    <div className={styles.wrapper}>
      <div className={styles.inner}>
        <div className={styles.content}>
          <div className={styles.vignetteWrapper}>
            {firstLoadDone ? (
              <div className={styles.vignetteInner}>
                <div className={styles.logoWrapper}>
                  <div className={styles.logoInner}>
                    <Logo />
                  </div>
                  <div className={styles.ctaHeader}>
                    {canDismissOnboarding ? (
                      <Button
                        type="link"
                        className={styles.logout}
                        icon={<CloseOutlined />}
                        onClick={() => {
                          close();
                        }}
                      >
                        Close
                      </Button>
                    ) : (
                      <Button
                        type="link"
                        className={styles.logout}
                        onClick={() => {
                          logout(() => {
                            navigate('/login');
                            clearProfile();
                          });
                        }}
                      >
                        Logout
                      </Button>
                    )}
                  </div>
                </div>
                {getStepContent()}
              </div>
            ) : (
              <div className={styles.loader}>
                <Loader />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
